import { INPUT_ZONES } from './zones';
import { screenToGameFlipper } from './pinball';

/**
 * Pointer input for flippers.
 * Left half of screen = left flipper, right half = right flipper.
 * Uses Pointer Events for unified touch/mouse/pen handling.
 *
 * NOTE: Screen-space sides are mapped to game-space via pinball orientation.
 * Bottom portion of screen triggers flippers (avoids conflict with drag-tilt).
 */
export class TouchInput {
  leftFlipper = false;
  rightFlipper = false;

  private activePointers = new Map<number, 'left' | 'right'>();
  private element: HTMLElement | null = null;

  /** Attach to a canvas/element */
  attach(element: HTMLElement): void {
    this.element = element;

    // Prevent browser gestures from stealing touches
    element.style.touchAction = 'none';

    // Pointer events unify touch, mouse, and pen
    element.addEventListener('pointerdown', this.handlePointerDown);
    element.addEventListener('pointerup', this.handlePointerUp);
    element.addEventListener('pointercancel', this.handlePointerUp);

    // Keyboard fallback
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
  }

  private handlePointerDown = (e: PointerEvent): void => {
    if (!this.element) return;
    const rect = this.element.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Only the bottom region activates flippers (avoids conflict with drag tilt)
    if (y < rect.height * INPUT_ZONES.flipperZoneTop) return;

    // Map screen side to game flipper via pinball orientation
    const screenSide = x < rect.width / 2 ? 'left' : 'right';
    const side = screenToGameFlipper(screenSide);

    this.activePointers.set(e.pointerId, side);
    this.element.setPointerCapture(e.pointerId);

    this.updateFlippers();
  };

  private handlePointerUp = (e: PointerEvent): void => {
    this.activePointers.delete(e.pointerId);
    this.updateFlippers();
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    // A = left screen side, L = right screen side
    // Mapped through pinball orientation
    if (e.code === 'KeyA' || e.code === 'ArrowLeft') {
      const side = screenToGameFlipper('left');
      if (side === 'left') this.leftFlipper = true;
      else this.rightFlipper = true;
    }
    if (e.code === 'KeyL' || e.code === 'ArrowRight') {
      const side = screenToGameFlipper('right');
      if (side === 'left') this.leftFlipper = true;
      else this.rightFlipper = true;
    }
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    if (e.code === 'KeyA' || e.code === 'ArrowLeft') {
      const side = screenToGameFlipper('left');
      if (side === 'left') this.leftFlipper = false;
      else this.rightFlipper = false;
    }
    if (e.code === 'KeyL' || e.code === 'ArrowRight') {
      const side = screenToGameFlipper('right');
      if (side === 'left') this.leftFlipper = false;
      else this.rightFlipper = false;
    }
  };

  private updateFlippers(): void {
    this.leftFlipper = false;
    this.rightFlipper = false;

    for (const side of this.activePointers.values()) {
      if (side === 'left') this.leftFlipper = true;
      if (side === 'right') this.rightFlipper = true;
    }
  }

  dispose(): void {
    if (this.element) {
      this.element.removeEventListener('pointerdown', this.handlePointerDown);
      this.element.removeEventListener('pointerup', this.handlePointerUp);
      this.element.removeEventListener('pointercancel', this.handlePointerUp);
    }
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
  }
}
